/**
 * Transaction Import Service
 * 
 * Service for importing transactions from CSV files
 * Handles CSV parsing, column mapping, duplicate detection, and batch import
 */

import { supabase } from '../lib/supabase';
import { getCurrentUserId } from '../lib/supabase';
import { parseCSVFile as parseCSVFileUtil } from '../utils/csv-parser';
import type {
  ImportTransactionsRequest,
  ParsedTransaction,
  ColumnMapping,
  ParseCSVResponse,
  ServiceResponse
} from '../types/import.types';
import { transactionService } from './transaction-service';

/**
 * Parse CSV file and return preview
 */
export async function parseCSVFile(file: File): Promise<ServiceResponse<ParseCSVResponse>> {
  try {
    // Validate file size (max 10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_FILE_SIZE) {
      return {
        error: {
          code: 'FILE_TOO_LARGE',
          message: `File size exceeds maximum of ${MAX_FILE_SIZE / 1024 / 1024}MB`
        }
      };
    }

    // Validate file format
    const validExtensions = ['.csv', '.txt'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

    if (!hasValidExtension && file.type !== 'text/csv' && file.type !== 'text/plain') {
      return {
        error: {
          code: 'INVALID_FILE_FORMAT',
          message: 'File must be a CSV file'
        }
      };
    }

    // Parse CSV file
    const result = await parseCSVFileUtil(file);

    return {
      data: {
        columns: result.columns,
        rows: result.rows,
        separator: result.separator
      }
    };
  } catch (error: any) {
    return {
      error: {
        code: 'PARSE_ERROR',
        message: error.message || 'Failed to parse CSV file'
      }
    };
  }
}

/**
 * Detect duplicates in parsed transactions
 */
export async function detectDuplicates(
  transactions: ParsedTransaction[],
  accountId: string,
  userId: string
): Promise<Array<{ transaction: ParsedTransaction; existingId: string }>> {
  const duplicates: Array<{ transaction: ParsedTransaction; existingId: string }> = [];

  for (const transaction of transactions) {
    try {
      // Try to find duplicate by external_id first
      if (transaction.externalId) {
        const { data: existing, error } = await supabase
          .from('transactions')
          .select('id')
          .eq('account_id', accountId)
          .eq('user_id', userId)
          .eq('external_id', transaction.externalId)
          .limit(1)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          // PGRST116 is "not found" which is expected, ignore it
          console.warn('Error checking duplicate by external_id:', error);
        }

        if (existing) {
          duplicates.push({ transaction, existingId: existing.id });
          continue;
        }
      }

      // Fallback: find duplicate by date + amount + description
      const { data: existing, error } = await supabase
        .from('transactions')
        .select('id')
        .eq('account_id', accountId)
        .eq('user_id', userId)
        .eq('date', transaction.date)
        .eq('amount', transaction.amount)
        .ilike('description', transaction.description)
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 is "not found" which is expected, ignore it
        console.warn('Error checking duplicate by date+amount+description:', error);
      }

      if (existing) {
        duplicates.push({ transaction, existingId: existing.id });
      }
    } catch (error) {
      // Silently continue if there's an error checking for duplicates
      // This prevents import from failing due to duplicate check errors
      console.warn('Error in duplicate detection:', error);
    }
  }

  return duplicates;
}

/**
 * Process batch import (100 transactions per batch)
 */
async function processBatchImport(
  transactions: ParsedTransaction[],
  accountId: string,
  userId: string,
  importSessionId: string,
  settings: { skipDuplicates?: boolean; autoCategorise?: boolean; skipZeroAmounts?: boolean },
  onProgress?: (progress: number) => void
): Promise<{ imported: number; failed: number; skippedDuplicates: number; errors: string[] }> {
  const BATCH_SIZE = 100;
  let imported = 0;
  let failed = 0;
  let skippedDuplicates = 0;
  const errors: string[] = [];

  // Filter out zero amounts if skipZeroAmounts is enabled
  let transactionsToImport = transactions;
  if (settings.skipZeroAmounts) {
    transactionsToImport = transactions.filter(t => t.amount > 0);
  }

  // Detect duplicates if skipDuplicates is enabled
  const duplicateMap = new Map<string, string>();
  if (settings.skipDuplicates) {
    const duplicates = await detectDuplicates(transactionsToImport, accountId, userId);
    for (const dup of duplicates) {
      duplicateMap.set(dup.transaction.externalId || `${dup.transaction.date}-${dup.transaction.amount}-${dup.transaction.description}`, dup.existingId);
    }
  }

  // Process in batches
  for (let i = 0; i < transactionsToImport.length; i += BATCH_SIZE) {
    const batch = transactionsToImport.slice(i, i + BATCH_SIZE);
    // Intentionally unused, kept for future logging
    const batchNumber = Math.floor(i / BATCH_SIZE) + 1;
    const totalBatches = Math.ceil(transactionsToImport.length / BATCH_SIZE);
    void batchNumber;
    void totalBatches;

    for (let j = 0; j < batch.length; j++) {
      const transaction = batch[j];
      if (!transaction) continue;
      const rowNumber = i + j + 1;

      try {
        // Skip if duplicate
        const duplicateKey = transaction.externalId || `${transaction.date}-${transaction.amount}-${transaction.description}`;
        if (duplicateMap.has(duplicateKey)) {
          skippedDuplicates++;
          continue; // Skip duplicate
        }

        // Skip transfer transactions - they require source and destination accounts
        // which are not available in the CSV import format
        if (transaction.type === 'transfer') {
          failed++;
          errors.push(`Row ${rowNumber}: Source and destination accounts are required for transfer transactions`);
          continue;
        }

        // Create transaction
        const result = await transactionService.createTransaction({
          accountId,
          amount: transaction.amount,
          description: transaction.description,
          date: transaction.date,
          type: transaction.type,
          merchantName: transaction.merchantName,
          referenceNumber: transaction.referenceNumber,
          externalId: transaction.externalId,
          originalDescription: transaction.description,
          balanceAfter: transaction.balanceAfter,
          chequeNumber: transaction.chequeNumber
        });

        if (result.error) {
          failed++;
          errors.push(`Row ${rowNumber}: ${result.error.message}`);
        } else {
          imported++;
        }
      } catch (error: any) {
        failed++;
        errors.push(`Row ${rowNumber}: ${error.message || 'Unknown error'}`);
      }
    }

    // Update progress
    if (onProgress) {
      const progress = Math.round(((i + batch.length) / transactionsToImport.length) * 100);
      onProgress(progress);
    }

    // Update import session
    await supabase
      .from('import_sessions')
      .update({
        imported_rows: imported,
        failed_rows: failed,
        error_log: errors.join('\n')
      })
      .eq('id', importSessionId);
  }

  return { imported, failed, skippedDuplicates, errors };
}

/**
 * Import transactions from parsed data
 */
export async function importTransactions(
  request: ImportTransactionsRequest,
  onProgress?: (progress: number) => void
): Promise<ServiceResponse<{ id: string; status: string; totalRows: number; importedRows: number; failedRows: number; skippedDuplicates: number }>> {
  try {
    const userId = await getCurrentUserId();
    if (!userId) {
      return {
        error: {
          code: 'AUTH_ERROR',
          message: 'User not authenticated'
        }
      };
    }

    // Verify account belongs to user
    const { data: account, error: accountError } = await supabase
      .from('accounts')
      .select('id')
      .eq('id', request.accountId)
      .eq('user_id', userId)
      .single();

    if (accountError || !account) {
      return {
        error: {
          code: 'FORBIDDEN',
          message: 'Account not found or does not belong to user'
        }
      };
    }

    // Parse transactions from CSV rows using column mappings
    // Note: This assumes parsedTransactions are already provided in the request
    // In a real implementation, you would parse the CSV rows here using the mappings
    // Intentionally unused, kept for future implementation
    const parsedTransactions: ParsedTransaction[] = [];
    void parsedTransactions;

    // Validate file size
    const fileSize = request.fileSize || 1; // Default to 1 byte if not provided (minimum valid size)
    if (fileSize <= 0 || fileSize > 10485760) {
      return {
        error: {
          code: 'INVALID_FILE_SIZE',
          message: `File size must be between 1 byte and 10MB (got ${fileSize} bytes)`
        }
      };
    }

    // Create import session
    const { data: session, error: sessionError } = await (supabase
      .from('import_sessions')
      .insert({
        user_id: userId,
        account_id: request.accountId,
        filename: request.filename || 'import.csv',
        file_size: fileSize,
        total_rows: request.parsedTransactions.length,
        imported_rows: 0,
        failed_rows: 0,
        status: 'processing',
        import_settings: request.settings
      } as any)
      .select()
      .single() as any);

    if (sessionError || !session) {
      return {
        error: {
          code: 'SESSION_ERROR',
          message: `Failed to create import session: ${sessionError?.message || 'Unknown error'}`
        }
      };
    }

    // Process import in batches
    const result = await processBatchImport(
      request.parsedTransactions,
      request.accountId,
      userId,
      session.id,
      request.settings,
      onProgress
    );

    // Update import session with final status
    const finalStatus = result.failed === request.parsedTransactions.length ? 'failed' : 'completed';
    await supabase
      .from('import_sessions')
      .update({
        imported_rows: result.imported,
        failed_rows: result.failed,
        status: finalStatus,
        error_log: result.errors.join('\n')
      })
      .eq('id', session.id);

    return {
      data: {
        id: session.id,
        status: finalStatus,
        totalRows: request.parsedTransactions.length,
        importedRows: result.imported,
        failedRows: result.failed,
        skippedDuplicates: result.skippedDuplicates
      }
    };
  } catch (error: any) {
    return {
      error: {
        code: 'UNKNOWN_ERROR',
        message: error.message || 'An unexpected error occurred'
      }
    };
  }
}

/**
 * Save column mappings by bank format
 */
export async function saveColumnMappings(
  bankFormat: string,
  mappings: ColumnMapping[]
): Promise<void> {
  // Store in localStorage for now (could be moved to database later)
  const key = `column_mappings_${bankFormat}`;
  localStorage.setItem(key, JSON.stringify(mappings));
}

/**
 * Load column mappings by bank format
 */
export async function loadColumnMappings(
  bankFormat: string
): Promise<ColumnMapping[] | null> {
  // Load from localStorage
  const key = `column_mappings_${bankFormat}`;
  const stored = localStorage.getItem(key);
  if (stored) {
    return JSON.parse(stored);
  }
  return null;
}

// Export service instance
export const transactionImportService = {
  parseCSVFile,
  importTransactions,
  detectDuplicates,
  saveColumnMappings,
  loadColumnMappings
};

